import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AutoAwesome, Delete } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, FormControl, Grid, Skeleton, TextField, Typography } from '@mui/material'

import { ImageCarouselModal } from '../../../components/ImageCarouselModal/ImageCarouselModal'
import { SelectMenu } from '../../../components/SelectMenu/SelectMenu'
import { displaySnackBar } from '../../../features/snackbar'
import { useAppDispatch } from '../../../hooks/storeHooks'
import { borderRadiusDefault } from '../../../themes/theme'
import { AIImageType, useGenerateAIImageMutation } from '../../api/live-event-predictions'

type GenerateAIImageToolProps = {
  imageUrl?: string
  userPrompt?: string
  imageType: AIImageType
  extraValue: string
  onImageUrlChange: (newImageUrl: string) => void
}

export const GenerateAIImageTool: FC<GenerateAIImageToolProps> = ({ imageUrl, userPrompt, imageType, extraValue, onImageUrlChange }) => {
  const { t } = useTranslation()
  const [extraPrompt, setExtraPrompt] = useState('')
  const [isLoadingAIImage, setIsLoadingAIImage] = useState(false)
  const [selectedImageType, setSelectedImageType] = useState<AIImageType>(imageType)
  const [imageCarouselModalOpen, setImageCarouselModalOpen] = useState(false)
  const dispatch = useAppDispatch()
  const [generateAIImage] = useGenerateAIImageMutation()
  const [errorMessage, setErrorMessage] = useState('')

  const handleGenerateAIImage = useCallback(() => {
    setErrorMessage('')
    const aiPrompt = extraPrompt ? `${userPrompt} ${'\n'} ${extraPrompt}` : userPrompt

    onImageUrlChange('')
    setIsLoadingAIImage(true)
    generateAIImage({ type: selectedImageType, prompt: aiPrompt || '', gameName: extraValue })
      .unwrap()
      .then((response) => {
        onImageUrlChange(response.imageUrl)
      })
      .catch((error) => {
        const errorMessage = error.message || error.error || t('internal-common:failed_to_generate_ai_image')
        setErrorMessage(errorMessage)
        dispatch(displaySnackBar({ message: t('internal-common:failed_to_generate_ai_image'), severity: 'error', open: true }))
      })
      .finally(() => {
        setIsLoadingAIImage(false)
      })
  }, [userPrompt, extraPrompt, selectedImageType, extraValue, generateAIImage, onImageUrlChange, dispatch, t])

  const handleExtraPromptChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = event.target.value
    setExtraPrompt(newSearchText)
  }, [])

  const deleteAIImage = useCallback(() => {
    onImageUrlChange('')
  }, [onImageUrlChange])

  const options = useMemo(() => {
    return [
      {
        name: t(`internal-common:ai_image_type__${AIImageType.LIVE_EVENT_COMMENT_MONTHLY_RECAP}`),
        value: AIImageType.LIVE_EVENT_COMMENT_MONTHLY_RECAP,
      },
    ]
  }, [t])

  const imageUrls = useMemo(() => {
    return imageUrl ? [imageUrl] : []
  }, [imageUrl])

  return (
    <Box>
      <Grid container spacing={2} wrap="wrap">
        <Grid item xs={12} md={'auto'} style={{ width: '400px' }}>
          {!imageUrl && <Skeleton variant="rounded" height={214} animation={isLoadingAIImage ? 'wave' : false} style={{ borderRadius: borderRadiusDefault }} />}
          {imageUrl && (
            <>
              <img
                className="ImageRoundedBorderWithShadow"
                alt="AI Generated"
                src={imageUrl}
                width="100%"
                style={{ cursor: 'pointer' }}
                onClick={() => setImageCarouselModalOpen(true)}
              />

              <ImageCarouselModal imageUrls={imageUrls} open={imageCarouselModalOpen} onClose={() => setImageCarouselModalOpen(false)} initialIndex={0} />
            </>
          )}
        </Grid>
        <Grid item xs>
          <Box mb={2}>
            <Grid container spacing={2} wrap="wrap">
              <Grid item>
                <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
                  <SelectMenu
                    label={t('internal-common:generate_ai_image_type')}
                    options={options}
                    value={selectedImageType}
                    disabled={isLoadingAIImage}
                    onChange={(e) => {
                      setSelectedImageType(e.target.value as AIImageType)
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item>
                <LoadingButton
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={handleGenerateAIImage}
                  disabled={!userPrompt}
                  loading={isLoadingAIImage}
                >
                  {t('internal-common:generate_ai_image')}
                </LoadingButton>
              </Grid>
              <Grid item>
                <Button variant="contained" color="error" startIcon={<Delete />} onClick={deleteAIImage} disabled={!imageUrl || isLoadingAIImage}>
                  {t('internal-common:delete_ai_image')}
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Box mb={2}>
            <TextField
              size="small"
              multiline
              rows={3}
              label={t('internal-common:generate_ai_image_extra_prompt')}
              variant="outlined"
              onChange={handleExtraPromptChange}
              fullWidth
              disabled={isLoadingAIImage}
            />
          </Box>
          <Typography variant="subtitle2" color="textSecondary">
            {t('internal-common:generate_ai_image_help_text')}
          </Typography>
        </Grid>
      </Grid>
      {errorMessage && (
        <Box mt={2}>
          <Typography variant="subtitle2" color="error" textAlign="center" fontWeight="bold">
            {errorMessage}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
